import $ from 'jquery';
import _ from 'lodash';
import { getLanguage, findVoice, findDefaultVoice, activateVoice } from 'view/insertion/cognitive-arrays';
import Data from 'app-core/Data';
import auth from 'auth';

const replaceSelfClosedTags = (text) => {
	let split = `${text}`.split('/>');
	if (split.length > 1) {
		let replacedText = split
			.filter(f => f)
			.map((f, i) => {
				if (split.length - 1 === i && f.substr(-2) !== '/>') return f;
				let tagsArr = f.split('<');
				let tag = tagsArr[tagsArr.length - 1].split(' ')[0];
				return `${f}></${tag}>`;
			})
			.join('');

		if (__DEBUG__)
			console.debug('Replace self closed tags', { text, replacedText });

		return replacedText;
	}
	return text;
}

function clearSsml(doc) {
	if (!doc?.querySelectorAll) return;
	[...doc.querySelectorAll('.highlighted')].map(node => {
		let replace = true;
		let attributes = [...node.attributes];
		for(var i = 0; i < attributes.length; i++) {
			var attr = attributes[i];
			if (['class', 'internal_id'].includes(attr.name)) continue;
			if (attr.name === 'pitch' && attr.value == "+0%") continue;
			if (__DEBUG__)
				console.debug('Clear highlighted tag 1', { name: attr.name, value: attr.value });
			replace = false;
			break;
		}
		if (__DEBUG__)
			console.debug('Clear highlighted tag', { node, replace });
		if (replace) {
			node.replaceWith(...node.childNodes);
		}
	});

	const selectedAfter = doc.querySelector('.selected-after');
	if (selectedAfter?.parentElement?.classList?.contains) {
		if (!selectedAfter.parentElement?.classList?.contains('highlighted') || selectedAfter.parentElement?.tagName !== 'PROSODY') {
			selectedAfter.remove();
		}
	}

	[...(doc.querySelectorAll('[internal_id]') || [])].map(f => f && f.removeAttribute && f.removeAttribute('internal_id'));
}

function getRootAimNodes(f) {
	if (!f) return [];
	return [...(f.childNodes || [])].filter(n => {
		let tag = tags[n.nodeName.toLowerCase()];
		if (tag?.doNotCalculateForRootProsody) {
			return false;
		}

		if (n.nodeName.toLowerCase() === '#text' && !(n.nodeValue || '').trim()) {
			return false;
		}
		
		return true;
	});
}

function prepareSsml(ssml, opts) {
	if (__DEBUG__)
		console.debug('Prepare ssml input', { ssml, opts });

	if (!ssml) return {
		ssml
	}

	let pitchSign = (opts?.pitch < 1 ? '-' : '+');
	let pitch = opts?.pitch || 1;
	let pitchValue = pitch < 1
		? (100 - pitch * 100).toFixed(0)
		: (pitch * 100 - 100).toFixed(0);
	let language = getLanguage(opts?.language);

	ssml = replaceSelfClosedTags(ssml);

	if (ssml.indexOf('<speak') === -1) {
		if (__DEBUG__)
			console.debug('No speak tag');

		if (`${ssml}`.startsWith('<prosody') && `${ssml}`.endsWith('</prosody>')) {
			let $ssml = $(ssml).attr('rate', opts?.rate || 1).attr('pitch', `${pitchSign}${pitchValue}%`);
			ssml = $('<div/>').append($ssml).html();
		} else {
			ssml = `<prosody rate="${opts?.rate || 1}" pitch="${pitchSign}${pitchValue}%">${ssml}</prosody>`;
		}
		ssml = `<speak xmlns="http://www.w3.org/2001/10/synthesis" xmlns:mstts="http://www.w3.org/2001/mstts" xmlns:emo="http://www.w3.org/2009/10/emotionml" version="1.0" xml:lang="${language}">\r\n` +
			`	<voice name="${opts?.voice}">\r\n` +
			`		<mstts:express-as ${opts?.voiceStyle ? `style="${opts?.voiceStyle}"` : ''}>\r\n` +
			`			${ssml}\r\n` +
			'		</mstts:express-as>\r\n' +
			'	</voice>\r\n' +
			'</speak>';
	}

	let htmlDoc = $('<div>').html(ssml);
	let speak = htmlDoc.find('speak');
	let voice = speak.find('> voice');
	let hasDefaultVoiceReplaced = false;
	let voiceStyle = opts?.voiceStyle || '';

	if (voice.length === 1) {
		if (opts?.language) {
			speak.attr('xml:lang', opts?.language);
			voice.attr('xml:lang', opts?.language);
		}

		let lang = voice.find('> lang');
		if (lang.length) {
			voice.html(lang[0].innerHTML);
		}

		if (opts?.voice) {
			voice.attr('name', opts?.voice);
			
			let selectedVoice = findVoice(opts?.voice, opts?.language);

			if (__DEBUG__)
				console.debug('Get ssml voice', { selectedVoice, language });

			if (selectedVoice && !selectedVoice?.isActive && opts?.isPreview) {
				activateVoice(selectedVoice, void 0, voiceStyle);
				let defaultVoice = findDefaultVoice(language, selectedVoice?.voice?.Gender, ssml);

				if (__DEBUG__)
					console.debug('Get ssml voice is resuming, replace by default', { defaultVoice });

				if (defaultVoice) {
					voice.attr('name', defaultVoice?.id);
					hasDefaultVoiceReplaced = true;
				}
			}
		}
		
		let mstts = getRootAimNodes(voice[0])?.length === 1 && voice.find('> mstts\\:express-as');
		if (!voiceStyle) {
			if (mstts.length) {
				mstts[0].after(...mstts[0].childNodes);
				mstts[0].remove();
				mstts = null;
			}
		}

		let prosody = getRootAimNodes(voice[0])?.length === 1 && voice.find('> prosody');
		if (!prosody?.length && mstts?.length) {
			prosody = getRootAimNodes(mstts[0])?.length === 1 && mstts.find('> prosody');
		}
		if (!prosody?.length) {
			prosody = document.createElement('prosody');
			[...((mstts[0] || voice[0]).childNodes || [])].map(f => prosody.appendChild(f));
			(mstts[0] || voice[0]).appendChild(prosody);

			prosody = $(prosody);
		}

		if (__DEBUG__)
			console.debug('Prepare ssml processing', { prosody, mstts, voiceStyle, voice, msttsL: getRootAimNodes(mstts && mstts[0]), voiceL: getRootAimNodes(voice && voice[0]) });

		if (prosody.length === 1) {
			prosody.attr('rate', opts?.rate || 1).attr('pitch', `${pitchSign}${pitchValue}%`);
			let lang = prosody.find('> lang');
			if (lang.length) {
				prosody.html(lang[0].innerHTML);
			}

			if (voiceStyle) {
				if (__DEBUG__)
					console.debug('Prepare ssml set voice style', { voiceStyle, mstts });
				if (!mstts.length) {
					let expressAsGlobal = document.createElement('mstts:express-as');
					expressAsGlobal.setAttribute('style', voiceStyle);
					expressAsGlobal.appendChild(prosody[0]);
					voice[0].innerHTML = expressAsGlobal.outerHTML;

					console.debug('Set voice style', { html: voice[0].innerHTML });
				} else {
					mstts.attr('style', voiceStyle);
				}
			}
		}

		//Leading silence
		const hasVoiceLeadingSilence = voice.attr('leadingsilence') || voice.attr('leadingsilence-exact');
		if (!hasVoiceLeadingSilence) {
			voice.attr('leadingsilence-exact', '0ms');
		} 
		voice.find('> mstts\\:silence[type="Leading-exact"]').remove();

		//Tailing silence
		const hasVoiceTailingSilence = voice.attr('tailingsilence') || voice.attr('tailingsilence-exact');
		if (!hasVoiceTailingSilence) {
			voice.attr('tailingsilence-exact', '0ms');
		}
		voice.find('> mstts\\:silence[type="Tailing-exact"]').remove();

		if(voice[0].outerHTML.indexOf('OverDub') > -1) {
			voice[0].outerHTML = voice[0].outerHTML.replace('leadingsilence-exact="0ms"', '').replace('tailingsilence-exact="0ms"', '');
		}

		let addLang = true;

		if (addLang && language) {
			let lang = document.createElement('lang');
			lang.setAttribute('xml:lang', language);

			lang.innerHTML = voice[0].innerHTML;
			voice[0].innerHTML = lang.outerHTML;
		}
	}

	clearSsml(htmlDoc[0]);

	if (opts?.afterProcess) {
		opts?.afterProcess({ htmlDoc, voice });
	}

	//There shouldn't be more than 1 spaces between tags
	speak.find('break').text('');
	[...(speak.find('lexicon'))].map(f => f.innerHTML = null);
	[...(speak.find('mstts\\:customtn'))].map(f => f.innerHTML = null);

	ssml = speak[0]?.outerHTML;
	ssml = `${ssml}`
		.replace(/\r/g, '')
		.replace(/\n/g, '')
		.replace(/\t/g, '')
		.replace(new RegExp('<br>', 'g'), '')
		.replace(new RegExp('<div>', 'g'), '')
		.replace(new RegExp('</div>', 'g'), '')
		.replace(new RegExp('&nbsp;', 'g'), ' ')
		.replace(/ {2,}/g, ' ');
	ssml = replaceSelfClosedTags(ssml);

	if (__DEBUG__)
		console.debug('Prepare ssml output', { ssml, voice: voice.attr('name'), hasDefaultVoiceReplaced, voiceObj: voice });

	let breaks = [...(ssml.match(/\<break(.*?)>(\s*)<\/break\>/g) || [])];
	breaks.map(f => {
		ssml = ssml.replace(f, f.replace('</break>', '').replace('>', '/>'));
	});

	return {
		ssml,
		voice: voice.attr('name'),
		hasDefaultVoiceReplaced
	}
}

function replaceTextInHtml(text) {
	let joinedObj = $('<div>').append(text);
	joinedObj.find('*').map((i, obj) => {
		let $obj = $(obj);
		$obj.contents()
			.each((ci, c) => {
				if (c.nodeType === 3) {
					$(c).remove();
				}
			});
	});

	[...(joinedObj.find('phoneme') || [])].map(phoneme => {
		if (!phoneme.innerText) {
			phoneme.remove();
		}
	});
	return joinedObj.html();
}

function splitSsmlSentences(baseSsml) {
	let sentences = [];
	let lastChar = null;
	let brackets = 0;
	let htmlPart = '';
	let sentenceItem = {};
	let isSayAs = false;
	let squareBrackets = 0;

	for (let charIdx = 0; charIdx < baseSsml.length; charIdx++) {
		let char = baseSsml[charIdx];
		let nextChar = baseSsml[charIdx + 1];

		if (char === '<')
			brackets++;

		htmlPart += char;

		if (htmlPart.endsWith('<say-as')) {
			isSayAs = true;
		}

		if (!brackets) {
			if (sentenceItem.textStart == null) {
				sentenceItem.textStart = charIdx;
			}
			if (!squareBrackets && !isSayAs && !/[.?!]+$/.test(nextChar)) {
				if (/[.?!]+$/.test(char) && /^[^<>)(!&?*\./\\]+$/.test(lastChar)) {
					sentences.push(sentenceItem);
					sentenceItem.htmlPart = htmlPart;
					htmlPart = '';
					sentenceItem = {}
				}
				lastChar = char;
			}
			if (sentenceItem.textStart != null && !/[<>]+$/.test(nextChar)) {
				sentenceItem.textEnd = charIdx + 1;
				sentenceItem.length = sentenceItem.textEnd - sentenceItem.textStart + 1;
			}
			
			if (char === '[') squareBrackets++;
			if (char === ']' && squareBrackets > 0) {
				squareBrackets--;
			}
		}

		if (htmlPart.endsWith('</say-as>')) {
			isSayAs = false;
		}

		if (char === '>' && brackets > 0) {
			brackets--;

			if (!brackets) {
				squareBrackets = 0;
			}
		}
	}

	if (sentenceItem.textStart != null && sentences.indexOf(sentenceItem) === -1)
		sentences.push(sentenceItem);

	if (__DEBUG__)
		console.debug('Split ssml sentences', { sentences });

	return sentences.filter(f => f.length).map(f => {
		let ssml = `${baseSsml}`;
		[...sentences]
			.reverse()
			.map(s => {
				if (s.textStart !== f.textStart && s.length) {
					ssml = replaceEmptyTags(replaceInString(ssml, s.textStart, s.textEnd));
				}
			});

		return ssml;
	});
}

function replaceInString(str, from, to, replaceTo) {
	return `${str.substr(0, from)}${replaceTo || ''}${str.substr(to + 1, str.length - 1)}`;
}

function replaceEmptyTags(ssml) {
	var emptyTextTags = ['phoneme'];

	var item = $('<div>').append(ssml);
	emptyTextTags.map(f => {
		let tags = item.find(f);
		tags.map(i => {
			let $el = $(tags[i]);
			if (!`${$el.text()}`.trim()) {
				$el.remove();
			}
		})
	});

	return item.html();
}

function parseContourPoints(val) {
	let points = [];
	if (val)
		try {
			let values = val.split(') (');
			values.map(v => {
				let value = v.replace(/[()%]+/g, '');
				let split = value.split(',');
				if (__DEBUG__)
					console.debug('Parse values', { v, val });
				let x = parseInt(split[0]);
				let y = parseInt(split[1]);
				points.push({
					id: x === 0 ? 'start' : x === 100 ? 'end' : this.$Data.uuid(),
					x,
					y
				});
			});
		} catch (ex) {
			if (__DEBUG__)
				console.debug('Parsing input values error', { ex });
		}

	if (!points.some(f => f.x === 0))
		points.push({
			id: 'start',
			x: 0,
			y: 0
		});

	if (!points.some(f => f.x === 100))
		points.push({
			id: 'end',
			x: 100,
			y: 0
		})
	return points;
}

function calcContourPointsValue(points) {
	let filteredPoints = points.filter(f => (f.x > 0 && f.x < 100) || f.y !== 0);
	let calcContourValue = _.orderBy(filteredPoints, ['x', 'y'])
		.map(f => `(${parseFloat(f.x).toFixed(0)}%, ${f.y >= 0 ? '+' : '-'}${Math.abs(parseFloat(f.y).toFixed(0))}%)`)
		.join(' ');

	if (__DEBUG__)
		console.debug('Contour value changed', calcContourValue);

	return calcContourValue === '(0%, 0%) (100%, 0%)' ? '' : calcContourValue;
}

async function generateSSML(opts) {
	let { ssml, rate, pitch, language, voice, isPreview, ttsProviderType, voiceSettings } = opts;

	const preparedSsml = prepareSsml(ssml, 
	{
		rate: rate || 1,
		pitch: pitch || 0,
		language: language || 'en-US',
		voice: voice || 'en-US-JennyMultilingualNeural',
		isPreview
	});

	const r = await Data.postExternalApi('Creative/BuildAudioCustom', {
		Language: language || 'en-US',
		Rate: rate || 1,
		Pitch: pitch || 0,
		Voice: preparedSsml.voice,
		Ssml: preparedSsml.ssml,
		TtsProviderType: ttsProviderType,
		ClientProcessId: auth.getGlobalProcessId(),
		VoiceSettings: voiceSettings
	}).catch(() => {});

	const audioBase64 = r?.AudioData && `data:audio/wav;base64,${r?.AudioData}`;
	return {
		base64: audioBase64,
		insertion: r?.SsmlInsertion,
		hasDefaultVoiceReplaced: preparedSsml.hasDefaultVoiceReplaced
	};
}

const tags = {
	'prosody': {
		attributes: [
			{ id: 'contour', hideValue: true, caption: 'Intonation' },
			{ id: 'pitch' },
			{ id: 'range' }, 
			{ id: 'rate' },
			{ id: 'volume', format: '__VALUE__%', requireStroke: true }
		],
		defaults: {
			pitch: '+0%',
			rate: 1,
			volume: 100
		},
		contentRequired: true,
		perAttributeEditing: true
	},
	'emphasis': {
		attributes: [{ id: 'level', caption: 'Emphasis' }],
		contentRequired: true,
		defaults: {
			level: 'none'
		},
		value: (f) => `Emphasis ${f.getAttribute('level')}`
	},
	'phoneme': {
		attributes: [{ id: 'alphabet', hide: true }, { id: 'ph', caption: 'Phoneme', hideValue: true }],
		supportsSubTags: false,
		contentRequired: true,
		caption: 'Phoneme',
		value: (f) => `Phoneme "${f.getAttribute('ph')}"`
	},
	'say-as': {
		attributes: [{ id: 'interpret-as', caption: 'Say-as' }, { id: 'format', hide: true }, { id: 'detail', hide: true }],
		contentRequired: true,
		caption: 'Say-as',
		value: (f) => `Say-as ${f.getAttribute('interpret-as')}`
	},
	'break': {
		attributes: [{ id: 'time', caption: 'Break', format: '__VALUE__ms' }],
		defaults: {
			time: '0.3s'
		},
		removeContent: true,
		showAlways: true,
		caption: 'break',
		value: (f) => `Break ${f.getAttribute('time')}`,
		contentEditable: false
	},
	'mstts:silence': {
		attributes: [{ id: 'value', caption: 'Boundary', format: '__VALUE__ms' }, { id: 'type', hide: true }],
		defaults: {
			value: 0
		},
		removeContent: true,
		showAlways: true,
		caption: 'Boundary',
		value: (f) => `Boundary ${f.getAttribute('value')}`
	},
	'mstts:express-as': {
		attributes: [{ id: 'style' }, { id: 'styledegree' }],
		contentRequired: true,
		perAttributeEditing: true
	},
	'adthos:wbs': {
		attributes: [
			{ id: 'id', hide: true }, { id: 'outparam', useTagEditorValue: true }, { id: 'defaultText', hide: true }, { id: 'templateField', hide: true }, 
			{ id: 'templateFieldsAssociation', json5: true, hide: true }, { id: 'listOptions', hide: true }, { id: 'customValues', json5: true, hide: true }
		],
		uiAttributes: [{ id: 'replaceAllOccurencies', default: false }, { id: 'paramsListOptionsVisible', default: false }, { id: 'hideServiceSelection', default: true }, { id: 'inparamCaptions', showAsEditorValue: true }],
		defaults: {
			templateFieldsAssociation: '{}',
			listOptions: '{}',
			customValues: '[]'
		},
		beforeEdit: (node, values) => {
			let inparam = {};
			let inparamCaptions = {};
			[...(node.querySelectorAll('inparam') || [])].map(f => {
				if (f.hasAttribute('name')) {
					inparam[f.getAttribute('name')] = f.getAttribute('val');
					if (f.hasAttribute('caption'))
						inparamCaptions[f.getAttribute('name')] = f.getAttribute('caption');
				}
			});

			values.inparam = inparam;
			values.inparamCaptions = inparamCaptions;
		},
		afterEdit: (node, values) => {
			if (__DEBUG__)
				console.debug('After edit', { node, values });
			[...(node.querySelectorAll('inparam') || [])].map(f => f.remove());
			for (let param in values.inparam) {
				let inparam = document.createElement('inparam');
				inparam.setAttribute('name', param);
				inparam.setAttribute('val', values.inparam[param] || '');
				if (values.inparamCaptions[param]) {
					inparam.setAttribute('caption', values.inparamCaptions[param]);
				}
				node.appendChild(inparam);
			}
			if (__DEBUG__)
				console.debug('After edit 2', { node, values });
		},
		value: (node) => {
			let inparamCaptions = [`${node.getAttribute('outparam') || 'Empty'}`];
			[...(node.querySelectorAll('inparam') || [])].map(f => {
				if (f.hasAttribute('name')) {
					let v = Data.limitText(f.getAttribute('caption') || f.getAttribute('val'), 10);
					if (v && !inparamCaptions.includes(v)) {
						inparamCaptions.push(v);
					}
				}
			});

			return inparamCaptions.join(', ');
		},
		removeContent: true,
		showAlways: true,
		contentEditable: false,
		isContent: true
	},
	'adthos:audio': {
		attributes: [
			{ id: 'id', hide: true }, { id: 'azureFile', useTagEditorValue: true }, { id: 'type', caption: 'NLCS' }, { id: 'intensity', hide: true }
		],
		defaults: {
			intensity: 0
		},
		removeContent: true,
		showAlways: true,
		caption: 'NLCS Sound',
		value: (f) => `NLCS Sound ${f.getAttribute('type')}`,
		isContent: true
	},
	'adthos:variativetext': {
		attributes: [
			{ id: 'variants', htmlEncode: true, useTagEditorValue: true, caption: 'Variative text', hideValue: true }, { id: 'defaultText', hide: true }
		],
		removeContent: true,
		showAlways: true,
		caption: 'Variative text',
		isContent: true
	},
	'lexicon': {
		attributes: [ { id: 'name', caption: 'lexicon' }, { id: 'uri', hide: true } ],
		removeContent: true,
		showAlways: true,
		value: (f) => `Lexicon: ${f.getAttribute('name')}`,
		doNotCalculateForRootProsody: true
	},
	'mstts:customtn': {
		attributes: [ { id: 'url', caption: 'customtn', useTagEditorValue: true, } ],
		removeContent: true,
		showAlways: true,
		caption: 'customtn',
		value: (node, doc) => {
			var elem = doc.querySelector('voice[customtn]');
			return `CustomTN: ${elem?.getAttribute('customtn') || 'customtn'}`;
		},
		doNotCalculateForRootProsody: true
	},
	'mstts:voiceconversion': {
		attributes: [ { id: 'url', caption: 'voiceconversion', useTagEditorValue: true, } ],
		removeContent: true,
		showAlways: true,
		caption: 'voiceconversion',
		isContent: true
	},
	'mstts:ttsembedding': {
		attributes: [ { id: 'prefixaudiocontent', caption: 'voiceconversion', useTagEditorValue: true, }, { id: 'prefixtext', hide: true },  { id: 'suffixaudiocontent', hide: true  }, { id: 'suffixext', hide: true } ],
		contentRequired: true,
		caption: 'Overdubbing',
		isContent: true
	},
	'adthos:stylizer': {
		attributes: [
			{ id: 'id', hide: true }, { id: 'name', caption: 'Stylizer' }
		],
		contentRequired: true,
		perAttributeEditing: true,
		caption: 'Stylizer'
	},
}

const tagsList = Object.keys(tags);

function clearDom(doc) {
	if (!doc) return;

	[...(doc.querySelectorAll('[class]') || [])].map(f => {
		f.removeAttribute('class');
	});

	[...(doc.querySelectorAll('[editorValue]') || [])].map(f => {
		f.removeAttribute('editorValue');
	});

	[...(doc.querySelectorAll('[action-source-id]') || [])].map(f => {
		f.removeAttribute('action-source-id');
	});

	[...(doc.querySelectorAll('[support-sub-tags]') || [])].map(f => {
		f.removeAttribute('support-sub-tags');
	});

	[...(doc.querySelectorAll('[contenteditable]') || [])].map(f => {
		f.removeAttribute('contenteditable');
	});

	[...(doc.querySelectorAll('span') || [])].map(f => {
		f.after(...f.childNodes);
		f.remove();
	});

	[...(doc.querySelectorAll('break') || [])].map(f => {
		if (f.childNodes?.length) {
			f.after(...f.childNodes);
		}
	});

	[...(doc.querySelectorAll('lang') || [])].map(f => {
		f.after(...f.childNodes);
		f.remove();
	});

	[...(doc.querySelectorAll('editor-action') || [])].map(f => {
		f.remove();
	});

	[...(doc.querySelectorAll('[data-jodit-selection_marker]') || [])].map(f => {
		f.remove()
	});

	let processNode = (f) => {
		if (f.nodeName.toLowerCase() === '#text') {
			f.textContent = replaceBOMChars(f.textContent);
			if (!f.textContent) {
				f.remove();
			}
		} else {
			let tag = tags[f.nodeName.toLowerCase()];

			if (tag) {
				let filledAttributesCount = 0;
				if (tag.attributes?.length) {
					tag.attributes.map(attr => {
						let nodeAttrId = attr.id.toLowerCase();
						let attrIsEmpty = !f?.attributes[nodeAttrId]?.nodeValue;
						if (!attrIsEmpty || tag.showAlways) {
							filledAttributesCount++;
						}
					});
				}
				
				if (!filledAttributesCount && !tag.showAlways) {
					f.after(...f.childNodes);
					f.remove();
					return;
				}

				if (tag.contentRequired && !(f.textContent || '').trim().length) {
					try {
						let hasContentTag = tagsList.filter(tag => tags[tag].isContent).find(tag => f.querySelector(`${tag.replace(/:/g, '\\:')}`));
						if (hasContentTag) {
							return;
						}
					}
					catch {
						//
					}
					f.remove();
					return;
				}
			}
		}
		
		[...(f.childNodes || [])].map(n => processNode(n));
	};

	processNode(doc);
}

function replaceBOMChars(str) {
	str = str || '';

	let result = '';
	for(let i = 0; i < str.length; i++) {
		if (![0xFEFF].includes(str.charCodeAt(i))) {
			result += str[i];
		}
	}
	return result;
}

function preformatDom(doc) {
	if (__DEBUG__)
		console.debug('preformatDom');

	if (!doc) return;

	clearDom(doc);

	[...(doc.querySelectorAll('s') || [])].map(f => {
		f.remove()
	});

	let processNode = (f) => {
		try {
			let tag = tags[f.nodeName.toLowerCase()];

			if (__DEBUG__)
					console.debug(`Process node ${f.nodeName}`, { tag, childNodes: [...f.childNodes], html: f.outerHTML });

			if (tag) {
				let filledAttributes = [];
				let filledAttributesIds = [];
				if (tag.attributes?.length) {
					tag.attributes.map(attr => {
						let nodeAttrId = attr.id.toLowerCase();
						let attrIsEmpty = !f?.attributes[nodeAttrId]?.nodeValue;
						if ((!attrIsEmpty || tag.showAlways) && !attr.hide) {
							if (!attr.hide) {
								filledAttributes.push(attr.hideValue ? `${attr.caption || attr.id}` : `${attr.caption || attr.id} ${f?.attributes[nodeAttrId]?.nodeValue}`);
							}
						}

						if (!attrIsEmpty) {
							filledAttributesIds.push(attr.id);
						}

						if (!attrIsEmpty && (attr.requireStroke || tag.requireStroke)) {
							f.after(document.createElement('s'));
							f.before(document.createElement('s'));
						}
					});
				}

				if (tag.contentEditable !== void 0) {
					f.setAttribute('contenteditable', tag.contentEditable);
				}

				if (filledAttributes.length) {
					f.setAttribute('editorValue', filledAttributes.join(' '));
				}

				if (tag.onPrepared) {
					tag.onPrepared(f, doc, filledAttributes);
				}

				if (!f.hasOwnProperty('editorValue')) {
					f.setAttribute('editorValue', tag.caption || f.nodeName.toLowerCase());
				}
				
				if (tag.hasOwnProperty('supportsSubTags')) {
					f.setAttribute('support-sub-tags', tag.supportsSubTags);
				}

				if (tag.value) {
					f.setAttribute('editorValue', tag.value(f, doc, filledAttributes));
				}

				if (f.nodeName.toLowerCase() === 'prosody' && filledAttributesIds.length === 2 && filledAttributesIds.includes('pitch') && filledAttributesIds.includes('rate')) {
					if (__DEBUG__)
						console.debug('Process node unwrapping', { rn: getRootAimNodes(f.parentNode), pRn: getRootAimNodes(f.parentNode?.parentNode) });
					if (['voice', 'lang'].includes(f.parentNode?.nodeName?.toLowerCase()) && getRootAimNodes(f.parentNode)?.length === 1) {
						f.after(...f.childNodes);
						f.remove();

						if (__DEBUG__)
							console.debug('Process node unwrapped');
					} else if (
						f.parentNode?.nodeName?.toLowerCase() === 'mstts:express-as' && getRootAimNodes(f.parentNode)?.length === 1 &&
						['voice', 'lang'].includes(f.parentNode?.parentNode?.nodeName?.toLowerCase()) && getRootAimNodes(f.parentNode?.parentNode)?.length === 1
					) {
						let parent = f.parentNode;
						f.after(...f.childNodes);
						f.remove();
						parent.after(...parent.childNodes);
						parent.remove();

						if (__DEBUG__)
							console.debug('Process node unwrapped with parent');
					}
				}

				if (__DEBUG__)
					console.debug('Process node', { f, parent: f.parentNode?.nodeName, pParent: f.parentNode?.parentNode?.nodeName, html: f.outerHTML, filledAttributesIds, tag, editorValue: f.getAttribute('editorValue'), nodesLength: f.childNodes?.length, nodes: f.childNodes, code: [...(f.childNodes || [])].map(n => [...(n.textContent || '')].map(c => c.charCodeAt(0))) });
			} else {
				if (f.nodeName.toLowerCase() === '#text' && f.previousSibling && f.previousSibling.nodeName.toLowerCase() !== '#text') {
					let nodeValue = (f.nodeValue || '').trim();
					if (['!', ',', '.', '!', '?', ':', ';'].includes(nodeValue)) {
						f.previousSibling.append(nodeValue);
						f.remove();
					}
				}
			}
		} finally {		
			[...(f.childNodes || [])].map(n => processNode(n));
		}
	};
	
	processNode(doc);

	let processTextNodes = (f) => {
		try {
			let nodes = [...(f.childNodes || [])].reverse();
			nodes.map((n, i) => {
				if (n.nodeName.toLowerCase() === '#text' && nodes[i + 1]?.nodeName?.toLowerCase() === '#text') {
					nodes[i + 1].nodeValue = `${nodes[i + 1].nodeValue}${n.nodeValue || ''}`;
					n.remove();

					if (__DEBUG__)
						console.debug('Merged text nodes');
				}
			})
		} finally {
			[...(f.childNodes || [])].map(n => processTextNodes(n));
		}
	}

	processTextNodes(doc);

	if (__DEBUG__)
		console.debug('preformatDom', { html: doc.outerHTML });

	let processEditorActions = (f) => {
		try {
			let tag = tags[f.nodeName.toLowerCase()];
			if (tag) {
				if (tag.attributes?.length) {
					tag.attributes.filter((f, i) => tag.perAttributeEditing || i === 0).map(attr => {
						let nodeAttrId = attr.id.toLowerCase();
						let attrIsEmpty = !f?.attributes[nodeAttrId]?.nodeValue;

						if ((!attrIsEmpty || tag.showAlways) && !attr.hide || !tag.perAttributeEditing) {
							var editorValue = attr.useTagEditorValue || !tag.perAttributeEditing ? f.getAttribute('editorValue') : (attr.hideValue ? `${attr.caption || attr.id}` : `${attr.caption || attr.id} ${f?.attributes[nodeAttrId]?.nodeValue}`);
							if (f.prepend && editorValue) {
								if (!f.getAttribute('action-source-id')) {
									f.setAttribute('action-source-id', Data.uuid());
								}
								let elem = f.querySelector(`:scope > editor-action[action-attribute="${attr.id}"]`);
								if (!elem) {
									elem = document.createElement('editor-action');
									f.prepend(elem);
									elem.setAttribute('action-attribute', attr.id);
									elem.after(' ');
									elem.before(' ');
								}
								elem.setAttribute('action-id', f.getAttribute('action-source-id'));
								elem.setAttribute('action-tag', f.nodeName.toLowerCase());
								elem.setAttribute('contenteditable', false);

								let text = editorValue;
								if (attr.format && tag.perAttributeEditing) {
									text = attr.format.replace(/\_\_VALUE\_\_/g, editorValue);
								}
								elem.innerText = `<${text}:`;
								elem.setAttribute('inner-text', elem.innerText);

								if (!f.getAttribute('editorValue')) {
									f.setAttribute('editorValue', text);
								}
							}
						}
					});
				}
				
			}
		} finally {
			[...(f.childNodes || [])].map(n => processEditorActions(n));
		}
	}

	processEditorActions(doc);
}

function preformatSsml(ssml) {
	let doc = document.createElement('div');
	doc.innerHTML = ssml;

	preformatDom(doc);
	
	return doc.innerHTML;
}

function clearPreformat(ssml) {
	let doc = document.createElement('div');
	doc.innerHTML = ssml;

	clearDom(doc);
	
	return doc.innerHTML;
}

function processSsmlSetting(ssml, settingName, value, opts) {
	let querySelector = opts?.selector || 'voice';

	let doc = document.createElement('div');
	doc.innerHTML = ssml;

	let elem = doc.querySelector(querySelector);
	if (!elem) return ssml;

	if (value !== void 0) {
		elem.setAttribute(settingName, value);
		ssml = doc.innerHTML;
	}

	return {
		ssml,
		value: elem.getAttribute(settingName)
	}
}

export default {
	replaceSelfClosedTags,
	replaceTextInHtml,
	prepareSsml,
	splitSsmlSentences,
	replaceInString,
	parseContourPoints,
	calcContourPointsValue,
	replaceEmptyTags,
	clearSsml,
	clearPreformat,
	generateSSML,
	preformatSsml,
	preformatDom,
	clearDom,
	tags,
	tagsList,
	processSsmlSetting
};
